import React, { useState, useEffect } from 'react';
import './Admin_DashBoard.css';
import SideBar from '../Components/SideBar';
import { API_BASE_URL } from "../config.js";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { FaUsers, FaUserMd, FaStethoscope, FaChartBar, FaSearch, FaBell } from 'react-icons/fa';

const Admin_DashBoard = () => {
  // 1. حالات الإحصائيات العامة (KPIs)
  const [kpiData, setKpiData] = useState({
    total_patients: 0,
    total_doctors: 0,
    active_cases: 0,
    monthly_visits: 0
  });

  // 2. حالات المخططات البيانية (Charts)
  const [governoratesRiskData, setGovernoratesRiskData] = useState([]);
  const [aiDiseaseRiskData, setAiDiseaseRiskData] = useState([]);

  // 3. حالات الجداول والبحث (Table & Search)
  const [recentVisits, setRecentVisits] = useState([]);
  const [doctorsList, setDoctorsList] = useState([]);
  const [patientsList, setPatientsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [serverSearchResults, setServerSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // 4. حالة التحميل
  const [isLoading, setIsLoading] = useState(true);

  // ألوان المخطط الدائري
  const PIE_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#3b82f6', '#10b981'];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const requestOptions = {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        };

        // أ. جلب بيانات الأطباء
        const doctorsRes = await fetch(`${API_BASE_URL}/doctors/`, requestOptions);
        const doctorsData = doctorsRes.ok ? await doctorsRes.json() : { length: 0 };
        setDoctorsList(doctorsData.results || (Array.isArray(doctorsData) ? doctorsData : []));

        // ب. جلب بيانات المرضى
        const patientsRes = await fetch(`${API_BASE_URL}/patients/`, requestOptions);
        const patientsData = patientsRes.ok ? await patientsRes.json() : { length: 0 };
        setPatientsList(patientsData.results || (Array.isArray(patientsData) ? patientsData : []));

        // ج. جلب بيانات الزيارات الحديثة
        const visitsRes = await fetch(`${API_BASE_URL}/visits/`, requestOptions);
        const visitsData = visitsRes.ok ? await visitsRes.json() : { length: 0 };
        const latestVisits = visitsData.results ? visitsData.results.slice(0, 20) : (Array.isArray(visitsData) ? visitsData.slice(0, 20) : []);
        setRecentVisits(latestVisits);

        // د. جلب تقارير الذكاء الاصطناعي للمخططات (من الإصدار الأول)
        let activeCasesCount = 0;
        try {
          const aiReportsRes = await fetch(`${API_BASE_URL}/reports/dashboard_summary/`, requestOptions);
          if (aiReportsRes.ok) {
            const aiData = await aiReportsRes.json();
            
            if (aiData.summary && aiData.summary.active_case_count) {
              activeCasesCount = aiData.summary.active_case_count;
            }

            if (aiData.alerts_by_disease) {
              const formattedAiData = aiData.alerts_by_disease
                .map(item => ({
                  name: item.disease__name,
                  risk_score: Number(parseFloat(item.max_risk_score || 0).toFixed(1)) 
                }))
                .slice(0, 5);
              setAiDiseaseRiskData(formattedAiData);
            }
          }
        } catch (aiError) {
          console.error("خطأ في جلب تقارير الذكاء الاصطناعي:", aiError);
        }

        // هـ. تعيين بيانات المحافظات (من الإصدار الأول)
        const syrianGovernorates = [
          { governorate: 'دمشق', risk_level: 85 },
          { governorate: 'ريف دمشق', risk_level: 78 },
          { governorate: 'حلب', risk_level: 72 },
          { governorate: 'حمص', risk_level: 60 },
          { governorate: 'حماة', risk_level: 50 },
          { governorate: 'اللاذقية', risk_level: 45 },
          { governorate: 'طرطوس', risk_level: 30 },
          { governorate: 'إدلب', risk_level: 65 },
          { governorate: 'الرقة', risk_level: 55 },
          { governorate: 'دير الزور', risk_level: 62 },
          { governorate: 'الحسكة', risk_level: 40 },
          { governorate: 'درعا', risk_level: 35 },
          { governorate: 'السويداء', risk_level: 25 },
          { governorate: 'القنيطرة', risk_level: 15 },
        ];
        setGovernoratesRiskData(syrianGovernorates);
        // و. تحديث الإحصائيات المجمعة
        setKpiData({
          total_patients: patientsData.count ?? patientsData.length,
          total_doctors: doctorsData.count ?? doctorsData.length,
          monthly_visits: visitsData.count ?? visitsData.length,
          active_cases: activeCasesCount
        });

        setIsLoading(false);
      } catch (error) {
        console.error("خطأ في جلب بيانات لوحة التحكم:", error);
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  // دوال مساعدة لجلب الأسماء بدلاً من المعرفات (IDs)
  const getPatientName = (patientId) => {
    const patient = patientsList.find(p => p.id === patientId);
    return patient ? patient.name : `مريض #${patientId}`;
  };

  const getDoctorName = (doctorId) => {
    const doctor = doctorsList.find(d => d.id === doctorId || d.user_id === doctorId);
    return doctor ? (doctor.real_name || doctor.name) : `طبيب #${doctorId}`;
  };

  // معالج البحث في الجدول (مدمج من الإصدار الثاني: بحث محلي + بحث سيرفر)
  const handleSearch = async (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (!term) {
      setServerSearchResults([]);
      return;
    }

    const searchLower = term.toLowerCase();
    
    // البحث أولاً في البيانات المحلية (آخر 20 زيارة)
    const hasLocalMatch = recentVisits.some(visit => 
      getPatientName(visit.patient).toLowerCase().includes(searchLower) || 
      visit.id.toString().includes(searchLower)
    );

    // إذا لم يتم العثور على النتيجة محلياً، يتم البحث في السيرفر
    if (!hasLocalMatch) {
      setIsSearching(true);
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/visits/?search=${term}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = res.ok ? await res.json() : [];
        setServerSearchResults(data.results || (Array.isArray(data) ? data : []));
      } catch (error) {
        console.error("خطأ أثناء البحث:", error);
      } finally {
        setIsSearching(false);
      }
    } else {
      setServerSearchResults([]);
    }
  };

  // تحديد الزيارات التي سيتم عرضها بناءً على نتيجة البحث
  const displayVisits = searchTerm && serverSearchResults.length > 0 
    ? serverSearchResults 
    : recentVisits.filter(visit => {
        const search = searchTerm.toLowerCase();
        return getPatientName(visit.patient).toLowerCase().includes(search) || visit.id.toString().includes(search);
      });

  return (
    <div className="admin-dashboard-layout">
      <SideBar role='admin' />
      
      <div className="admin-main-content">
       
       

        {isLoading ? (
           <div className="loading-spinner" style={{ textAlign: 'center', marginTop: '50px' }}>جاري تحميل البيانات من الخادم...</div>
        ) : (
          <>
            {/* 1. البطاقات الإحصائية (KPIs) */}
            <div className="kpi-cards-container">
              <div className="kpi-card">
                <div className="kpi-icon patients-icon"><FaUsers /></div>
                <div className="kpi-details"><p>إجمالي المرضى</p><h3>{kpiData.total_patients}</h3></div>
              </div>
              <div className="kpi-card">
                <div className="kpi-icon doctors-icon"><FaUserMd /></div>
                <div className="kpi-details"><p>إجمالي الأطباء</p><h3>{kpiData.total_doctors}</h3></div>
              </div>
              <div className="kpi-card">
                <div className="kpi-icon cases-icon"><FaStethoscope /></div>
                <div className="kpi-details"><p>الحالات النشطة</p><h3>{kpiData.active_cases}</h3></div>
              </div>
              <div className="kpi-card">
                <div className="kpi-icon visits-icon"><FaChartBar /></div>
                <div className="kpi-details"><p>الزيارات الشهرية</p><h3>{kpiData.monthly_visits}</h3></div>
              </div>
            </div>
            
            {/* 2. قسم المخططات البيانية (Charts) */}
            <div className="charts-section">
              <div className="chart-container main-chart" style={{ backgroundColor: '#fff', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '20px' }}>
                <h3 style={{ marginBottom: '20px', color: '#333' }}>توزع المخاطر حسب المحافظة </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={governoratesRiskData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                   <XAxis 
                      dataKey="governorate" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#888', fontSize: 11 }} // تصغير الخط ليناسب 14 محافظة
                      interval={0} // إجبار المخطط على عرض جميع الأسماء دون تخطي
                      angle={-45} // تدوير النص بزاوية 45 درجة لتجنب التداخل
                      textAnchor="end" // محاذاة النص بعد التدوير
                      height={60} // زيادة مساحة المحور السفلي ليتسع للنصوص المائلة
                      dy={10} 
                    />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#888' }} domain={[0, 100]} />
                    <RechartsTooltip cursor={{fill: 'transparent'}} contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} formatter={(value) => [`${value}%`, 'مستوى الخطر']} />
                    <Bar dataKey="risk_level" fill="#4F46E5" radius={[10, 10, 10, 10]} barSize={24} name="مستوى الخطر" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-container side-chart" style={{ backgroundColor: '#fff', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '20px' }}>
                <h3 style={{ textAlign: 'center', color: '#333', marginBottom: '20px' }}>الأمراض الأكثر انتشاراً (AI Predictions)</h3>
                {aiDiseaseRiskData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={aiDiseaseRiskData} cx="50%" cy="50%" innerRadius={70} outerRadius={90} paddingAngle={5} dataKey="risk_score" nameKey="name" stroke="none">
                        {aiDiseaseRiskData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }} formatter={(value) => [value, 'مستوى الخطر']} />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="no-data-msg" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>لا توجد بيانات كافية حالياً</p>
                )}
              </div>
            </div>

            {/* 3. قسم جدول المراجعات الحديثة مع البحث الذكي */}
            <div className="recent-table-section">
              <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3>الزيارات الحديثة</h3>
                <div className="table-search">
                  <FaSearch className="table-search-icon" />
                  <input 
                    type="text" 
                    className="table-search-input"
                    placeholder="ابحث برقم الزيارة أو اسم المريض..." 
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
              <div className="table-responsive">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>رقم الزيارة</th>
                      <th>اسم المريض</th>
                      <th>الطبيب المعالج</th>
                      <th>تاريخ التشخيص</th>
                      <th>الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isSearching ? (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>جاري البحث في قاعدة البيانات...</td></tr>
                    ) : displayVisits.length > 0 ? (
                      displayVisits.map((visit, index) => (
                        <tr key={index}>
                          <td>#{visit.id}</td>
                          <td>{getPatientName(visit.patient)}</td>
                          <td>{getDoctorName(visit.doctor || visit.doctor_id)}</td>
                          <td>{visit.diagnosis_date ? new Date(visit.diagnosis_date).toLocaleDateString('ar-EG') : 'غير محدد'}</td>
                          <td><span className="status-badge active">{visit.status || 'مسجلة'}</span></td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>لا توجد نتائج مطابقة.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Admin_DashBoard;